import { __awaiter, __generator } from "tslib";
import { fetchCapabilities } from '@office-start/capabilities-service';
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { fetchSharedContext, } from '@office-start/context-service';
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { fetchIdentity, getOfficeIdentity, } from '@office-start/identity-service';
import { fetchSearchConfig, } from '@office-start/search-config-service';
import { fetchIsSubscribedUser } from '@office-start/subscription-info-service';
import { logError } from '@office-start/telemetry-service';
import { fetchUserEntitlements, getUserEntitlements, } from '@office-start/user-entitlements-service';
import { DeferredPromise } from '../utils';
import { getCdnOptions, getHostAppId, getIsAirGaped, getIsGcc, getIsTestEnv, getRing, getSearchCloudsUrls, getSearchEnvironment, getSearchMiddleTierEnvironment, getTokenProviders, } from '.';
/**
 * Documentation for Search Settings:
 * https://dev.azure.com/Office/Office/_wiki/wikis/Microsoft-Search/88576/Office.com-Services
 **/
var SearchSettings = /** @class */ (function () {
    function SearchSettings() {
        var _this = this;
        this.officeComponentContext = new DeferredPromise();
        this.userEntitlements = new DeferredPromise();
        this.capabilities = new DeferredPromise();
        this.identity = new DeferredPromise();
        this.sharedContext = new DeferredPromise();
        this.isSubscribedUser = new DeferredPromise();
        /**
         * Settings:
         * 1. SearchConfig is a service that allows us to bridge
         *    the gap between the Angular and React versions by
         *    passing the settings from Angular into React, and also
         *    populating some values from ECS, if they exist in both.
         *    NOTE: Should be deprecated in favor of getting settings
         *          from ECS directly.
         * 2. Features gates service allows us to fetch the feature gates
         *    (boolean values) from ECS directly, and it does not care about
         *    Angular settings.
         */
        this.searchConfig = new DeferredPromise();
        this.featureGates = new DeferredPromise();
        // Helper methods
        this.fetchServiceSettings = function (fetcher, deferredPromise, name) { return __awaiter(_this, void 0, void 0, function () {
            var result, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, fetcher];
                    case 1:
                        result = _a.sent();
                        if (result !== undefined && result !== null) {
                            deferredPromise.resolve(result);
                        }
                        else {
                            this.rejectPromise(deferredPromise, name);
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        this.rejectPromise(deferredPromise, name);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); };
        this.rejectPromise = function (deferredPromise, name) {
            deferredPromise.reject("Failed to fetch ".concat(name, " settings"));
            logError({
                eventName: "".concat(name, "_Fetch_Error"),
                errorType: 'Search_Setting_Fetching_Error',
                area: 'SearchResults',
            });
        };
        this.getResultFromPromise = function (promise) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, promise().then(function (result) { return result === null || result === void 0 ? void 0 : result.result; })];
            });
        }); };
        // no-op
    }
    SearchSettings.prototype.initialize = function () {
        var userEntitlementsResult = getUserEntitlements();
        if (userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result) {
            this.userEntitlements.resolve(userEntitlementsResult.result);
        }
        else {
            this.fetchServiceSettings(this.getResultFromPromise(fetchUserEntitlements), this.userEntitlements, 'User_Entitlements');
        }
        var identityResult = getOfficeIdentity();
        if (identityResult) {
            this.identity.resolve(identityResult);
        }
        else {
            this.fetchServiceSettings(fetchIdentity(), this.identity, 'Identity');
        }
        this.fetchServiceSettings(fetchOfficeComponentContext(), this.officeComponentContext, 'Office_Component_Context');
        this.fetchServiceSettings(fetchCapabilities(), this.capabilities, 'Capabilities');
        this.fetchServiceSettings(fetchSearchConfig(), this.searchConfig, 'Search_Config');
        this.fetchServiceSettings(this.getResultFromPromise(fetchFeatureGates), this.featureGates, 'Feature_Gates');
        this.fetchServiceSettings(fetchSharedContext(), this.sharedContext, 'Shared_Context');
        this.fetchServiceSettings(fetchIsSubscribedUser(), this.isSubscribedUser, 'Is_Subscribed_User');
    };
    // Getters for services
    SearchSettings.prototype.getOfficeComponentContext = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.officeComponentContext.data];
            });
        });
    };
    SearchSettings.prototype.getUserEntitlements = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.userEntitlements.data];
            });
        });
    };
    SearchSettings.prototype.getCapabilities = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.capabilities.data];
            });
        });
    };
    SearchSettings.prototype.getSearchConfig = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.searchConfig.data];
            });
        });
    };
    SearchSettings.prototype.getIdentity = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.identity.data];
            });
        });
    };
    SearchSettings.prototype.getFeatureGates = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.featureGates.data];
            });
        });
    };
    SearchSettings.prototype.getSharedContext = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.sharedContext.data];
            });
        });
    };
    SearchSettings.prototype.getIsSubscribedUser = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.isSubscribedUser.data];
            });
        });
    };
    // Search specific custom settings
    SearchSettings.prototype.getSearchEnvironment = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getSearchEnvironment(this.getSearchConfig(), this.getUserEntitlements())];
            });
        });
    };
    SearchSettings.prototype.getIsAirGapped = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getIsAirGaped(this.getSearchEnvironment())];
            });
        });
    };
    SearchSettings.prototype.getSearchMiddleTierEnvironment = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getSearchMiddleTierEnvironment(this.getSearchEnvironment())];
            });
        });
    };
    SearchSettings.prototype.getSearchCloudUrls = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getSearchCloudsUrls(this.getSearchConfig(), this.getIsAirGapped())];
            });
        });
    };
    SearchSettings.prototype.getCdnOptions = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getCdnOptions(this.getSearchMiddleTierEnvironment())];
            });
        });
    };
    SearchSettings.prototype.getHostAppId = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getHostAppId(this.getCapabilities(), this.getIdentity())];
            });
        });
    };
    SearchSettings.prototype.getRing = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getRing(this.getSearchConfig())];
            });
        });
    };
    SearchSettings.prototype.getIsTestEnv = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getIsTestEnv(this.getOfficeComponentContext())];
            });
        });
    };
    SearchSettings.prototype.getIsGcc = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getIsGcc(this.getUserEntitlements())];
            });
        });
    };
    SearchSettings.prototype.getTokenProviders = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getTokenProviders(this.getOfficeComponentContext(), this.getUserEntitlements())];
            });
        });
    };
    return SearchSettings;
}());
export { SearchSettings };
//# sourceMappingURL=SearchSettings.js.map